import React, { useCallback, memo, useMemo, useState, useEffect } from 'react';
import { useFocus } from 'js/hooks/useFocus';
import { identity } from 'js/utils/ramda.util';
import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { areOmitedPropsEqual } from 'js/utils/methods.util';
import buildClassName from 'js/utils/classNameBuilder.util';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import { AccordionCell } from './components/AccordionCell';

import style from './AccordionRow.scss';

export const arePropsEqual = (prevProps, nextProps) => {
    const propsToOmit = ['bodyCells', 'headerCells'];

    return areOmitedPropsEqual(prevProps, nextProps, propsToOmit)
        && prevProps.bodyCells?.length === nextProps.bodyCells?.length
        && prevProps.headerCells?.length === nextProps.headerCells?.length;
};

/**
 * @typedef {Object} AccordionCell
 * @property {String} id
 * @property {*} [content] React compatible component
 * @property {String} [className] - several predefined global classes can be applied
 * @example
 * className: 'accordionHeaderSmallDetails'
 * className: 'accordionBodyCellName'
 * className: 'accordionBodyCellValue'
 * className: 'accordionHeaderMainDetail'
 * className: 'accordionHeaderHighlightDetail'
 */

/**
 * @typedef {Object} ClassNames
 * @property {String} [accordionContainer]
 * @property {String} [accordionHeaderContainer]
 * @property {String} [accordionHeaderCell]
 * @property {String} [accordionHeaderArrowRow]
 * @property {String} [accordionBodyContainer]
 * @property {String} [accordionBodyCell]
 */

/**
 * @param props {Object}
 * @param props.id {String}
 * @param props.bodyCells {AccordionCell[]}
 * @param props.headerCells {AccordionCell[]}
 * @param [props.onAccordionExpand] {Function}
 * @param [props.onAccordionCollapse] {Function}
 * @param [props.classNames] {ClassNames}
 * @param [props.isIndependentMode = true] {Boolean} - specifies only one or several items can be expanded at time
 * @param [props.expandItem] {Function} - required if isIndependentMode = false
 * @param [props.isExpanded] {Boolean} - required if isIndependentMode = false
 * @example
 * // when isIndependentMode = false, only one accordion row can be expanded at time
 * const [expandedItemId, setExpandedItemId] = useState(someId);
 * const expandItem = useCallback((id) => setExpandedItemId(id), []);
 * <AccordionRow id={id} expandItem={expandItem} isExpanded={expandedItemId === id} />
 * * @param [props.isFocused] {Boolean}
 */

export const AccordionRow = memo((props) => {
    const {
        id,
        bodyCells = [],
        headerCells = [],
        onAccordionExpand,
        onAccordionCollapse,
        classNames: propsClassNames,
        isExpandedByDefault = false,
        isIndependentMode = true,
        expandItem = identity,
        isExpanded = false,
        isFocused
    } = props;
    const translate = useTranslations();
    const isMounted = useIsMounted();
    const [rowRef, setFocus] = useFocus();
    const classNames = useMemo(() => propsClassNames || {}, [propsClassNames]);

    const [isRowExpanded, setIsRowExpanded] = useState(isExpandedByDefault);

    const toggleExpandedHandler = useCallback(
        (isExpandedState) => isExpandedState ? onAccordionExpand?.(id) : onAccordionCollapse?.(id),
        [onAccordionExpand, onAccordionCollapse, id]);

    const toggleExpanded = useCallback((event) => {
        event.preventDefault();

        const shouldToggleExpanded = event.target.closest(`.${style.accordionHeaderContainer}`)
            || event.target.classList.contains(style.accordionContainer);

        if (!isMounted() || !shouldToggleExpanded) {
            return;
        }

        if (isIndependentMode) {
            setIsRowExpanded((prev) => {
                toggleExpandedHandler(!prev);

                return !prev;
            });
        } else {
            expandItem(isExpanded ? null : id);
            toggleExpandedHandler(!isExpanded);
        }
    }, [expandItem, isExpanded, isIndependentMode, id, isMounted, toggleExpandedHandler]);

    useEffect(() => {
        if (isFocused) {
            setFocus();
        }
    });

    const isActuallyExpanded = isExpanded || isRowExpanded;

    return (
        <AccessibilityElement
            elementRef={rowRef}
            onClick={toggleExpanded}
            className={buildClassName(['accordionContainer'], style, classNames)}
            ariaLiveLabel={isActuallyExpanded
                ? translate('playtech.mobile.system.table.aria-label.collapse')
                : translate('playtech.mobile.system.table.aria-label.expand')
            }
        >
            <div
                aria-hidden={true}
                className={buildClassName(['accordionHeaderContainer'], style, classNames)}
            >
                {headerCells.map(cell => (
                    <AccordionCell
                        key={cell.id}
                        content={cell.content}
                        className={`${buildClassName(['accordionHeaderCell'], style, classNames)} ${cell.className || ''}`}
                    />
                ))}
                <div className={buildClassName(['accordionHeaderArrowRow'], style, classNames)}>
                    <Icon type="bottomListArrow" className={`${style.accordionIcon} ${isActuallyExpanded ? style.expanded : ''}`} />
                </div>
            </div>
            {isActuallyExpanded && (
                <div className={buildClassName(['accordionBodyContainer'], style, classNames)}>
                    {bodyCells.map(cell => (
                        <AccordionCell
                            key={cell.id}
                            content={cell.content}
                            className={`${buildClassName(['accordionBodyCell'], style, classNames)} ${cell.className || ''}`}
                        />
                    ))}
                </div>
            )}
        </AccessibilityElement>
    );
}, arePropsEqual);
